import { createContext, ReactNode, useCallback, useContext, useState } from "react";
import * as RadixToast from "@radix-ui/react-toast";
import "./Toast.css";

interface ToastMessage {
  id: string;
  title: string;
  description?: string;
  tone?: "success" | "danger" | "neutral";
}

interface ToastContextValue {
  showToast: (message: Omit<ToastMessage, "id">) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  const showToast = useCallback((message: Omit<ToastMessage, "id">) => {
    const id = crypto.randomUUID();
    setMessages((prev) => [...prev, { ...message, id }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      <RadixToast.Provider swipeDirection="right" duration={5000}>
        {children}
        {messages.map((message) => (
          <RadixToast.Root
            key={message.id}
            className={`nx-toast nx-toast--${message.tone ?? "neutral"}`}
            onOpenChange={(open) => {
              if (!open) dismiss(message.id);
            }}
          >
            <RadixToast.Title className="nx-toast__title">{message.title}</RadixToast.Title>
            {message.description ? (
              <RadixToast.Description className="nx-toast__description">
                {message.description}
              </RadixToast.Description>
            ) : null}
          </RadixToast.Root>
        ))}
        <RadixToast.Viewport className="nx-toast__viewport" />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
